/**
 * Result fingerprinting for cached analysis results (Field Debate, Coverage
 * Audit, Position Analysis). Each fingerprint captures the paper-set scope
 * the analysis ran against; on render we compare against the current scope
 * and surface STALE when divergence exceeds the threshold.
 *
 * See docs/superpowers/specs/2026-04-30-stale-result-fingerprinting-design.md.
 */

export interface ResultFingerprint {
  /** Sorted list of paper UUIDs that were in scope at generation time. */
  paper_ids: string[];
  /** Working set ID at generation time, or null if "all papers" was active. */
  ws_id: string | null;
  /** How many of the paper_ids were scored at generation time. */
  scored_count: number;
  /** ISO8601 timestamp of when the result was generated. */
  generated_at: string;
}

export interface ScopeSnapshot {
  paperIds: string[];
  wsId: string | null;
}

/** Default Jaccard divergence threshold above which a result is considered stale. */
export const STALE_THRESHOLD = 0.1;

export function computeFingerprint(opts: {
  paperIds: string[];
  wsId: string | null;
  scoredCount: number;
  generatedAt?: string;
}): ResultFingerprint {
  return {
    paper_ids: [...opts.paperIds].sort(),
    ws_id: opts.wsId,
    scored_count: opts.scoredCount,
    generated_at: opts.generatedAt ?? new Date().toISOString(),
  };
}

/**
 * Jaccard distance between the snapshot and the current paper set.
 * Returns 0 when identical, 1 when fully disjoint.
 */
export function divergence(
  snap: ResultFingerprint,
  currentPaperIds: string[],
): number {
  const snapSet = new Set(snap.paper_ids);
  const curSet = new Set(currentPaperIds);
  if (snapSet.size === 0 && curSet.size === 0) return 0;
  let inter = 0;
  for (const id of curSet) if (snapSet.has(id)) inter++;
  const union = snapSet.size + curSet.size - inter;
  return union === 0 ? 0 : 1 - inter / union;
}

/**
 * Stale if the working set changed (deliberate scope swap) OR Jaccard
 * divergence exceeds the threshold.
 */
export function isStale(
  snap: ResultFingerprint,
  current: ScopeSnapshot,
  threshold: number = STALE_THRESHOLD,
): boolean {
  if (snap.ws_id !== current.wsId) return true;
  return divergence(snap, current.paperIds) > threshold;
}
